import { NotificationService } from '@/services/notification.service';
import { PushService } from '@/services/push/push.service';
import { ensureSubscriptionUrl } from '@/services/subscriptionLink';
import { openUrl } from '@/utils/openUrl';
import {
  HOME_PATH,
  resolveLinkTarget,
  resolvePushTarget,
  sanitizePushEventData
} from '@/services/push/pushTarget';

// Roteador ÚNICO de "algo de fora quer abrir uma tela do app": toque em push,
// toque no toast de push em primeiro plano, Universal Link / App Link
// (`appUrlOpen` + `getLaunchUrl`) e o clique numa notificação do sino.
//
// Regras (STORY-1642, plano seção 8 "Toque"):
//   - o destino sai de função PURA (pushTarget) com allowlist de rota;
//   - nada navega antes do router estar pronto e do boot de auth terminar — o
//     main.js faz `replace('/p/my-courses')` com retentativa no boot, e uma
//     navegação concorrente era cancelada por ele (ou o cancelava);
//   - sem aluno logado, o destino fica PENDENTE e sai quando o login acontecer
//     (com validade, para um toque antigo não abrir a aula horas depois);
//   - a matrícula precisa estar na lista do aluno logado e virar a "current"
//     (setCurrentEnroll grava o X-ApplicationId global) ANTES de abrir a aula —
//     num deep link frio a lista ainda não carregou e o header ficaria com o
//     appId da matrícula anterior (ver CLAUDE.md, "X-ApplicationId");
//   - navegação sempre por `router.push`, nunca `window.location` (recarregar o
//     app perde estado e, no app, é uma tela branca).

const PENDING_TTL_MS = 15 * 60 * 1000;
const AUTH_WAIT_MAX_MS = 65 * 1000;
const LINK_DEDUPE_MS = 5000;

let _store = null;
let _router = null;
let _pending = null;
let _flushing = false;
const _recentLinks = new Map();

export function setupPushRouter ({ store, router }) {
  if (_store) return;
  _store = store;
  _router = router;
  // Destino pendente sai quando o aluno aparece (login) ou quando o boot de auth
  // termina — o que vier por último.
  store.watch(state => state.user.currentUser && state.user.currentUser.studentId, sid => {
    if (sid) flushPending();
  });
  store.watch(bootSettled, settled => {
    if (settled) flushPending();
  });
}

// Boot terminado: auth resolvido E o redirect do boot para Meus Cursos concluído.
// Só o `initializing` não basta: a rede de segurança de 5 s do main.js o libera
// com o redirect ainda tentando, e as duas navegações se cancelavam — a do boot
// vencia e o toque caía em Meus Cursos.
function bootSettled (state) {
  return !state.auth.initializing && !state.auth.bootRedirectPending;
}

function currentStudentId () {
  const user = _store && _store.state.user.currentUser;
  return (user && user.studentId) || null;
}

function routerReady () {
  return new Promise(resolve => _router.onReady(resolve, resolve));
}

function authSettled () {
  if (bootSettled(_store.state)) return Promise.resolve();
  return new Promise(resolve => {
    let done = false;
    const finish = () => { if (done) return; done = true; unwatch(); resolve(); };
    const unwatch = _store.watch(bootSettled, settled => { if (settled) finish(); });
    setTimeout(finish, AUTH_WAIT_MAX_MS);
  });
}

async function flushPending () {
  if (!_store || _flushing || !_pending) return;
  _flushing = true;
  try {
    while (_pending) {
      if (Date.now() - _pending.at > PENDING_TTL_MS) {
        _pending = null;
        break;
      }
      await routerReady();
      await authSettled();
      // Sem aluno: mantém pendente; o watcher do studentId chama de novo.
      if (!currentStudentId()) return;
      const item = _pending;
      _pending = null;
      try {
        await processItem(item);
      } catch (e) {
        console.warn('[push] falha ao abrir destino:', e?.message || e);
      }
    }
  } finally {
    _flushing = false;
  }
}

async function processItem (item) {
  if (item.kind === 'push') return openFromPush(item.data, item.options);
  if (item.kind === 'target') return navigateToTarget(item.target);
}

export function reportPushEvent (event, data) {
  if (!_store || !currentStudentId() || !_store.state.dom.isConnected) return;
  PushService.postEvent(event, sanitizePushEventData(data)).catch(() => {});
}

function markNotificationRead (nid) {
  if (!nid || !_store.state.dom.isConnected) return;
  NotificationService.read(nid)
    .then(() => _store.commit('notification/MARK_READ', nid))
    .catch(() => {});
}

async function openFromPush (data, options = {}) {
  const target = resolvePushTarget(data);
  // Push de outra conta (aparelho compartilhado) ou matrícula revogada: vai para
  // Meus Cursos SEM marcar o sino nem contar "opened". A SP de marcar lido não
  // confere o dono — antes, o aluno logado marcava como lida a notificação do
  // aluno que saiu.
  if ((target.name === 'class' || target.name === 'course') && !(await ensureEnroll(target))) {
    if (_router.currentRoute.path !== HOME_PATH) await go({ path: HOME_PATH });
    return;
  }
  if (!options.skipOpenedEvent) reportPushEvent('opened', data);
  if (target.nid) markNotificationRead(target.nid);

  await navigateToTarget(target);
}

function findEnroll (target) {
  const list = _store.state.enroll.enrolls || [];
  const byId = list.find(e => String(e.id) === String(target.enrollId));
  if (byId) return byId;
  // A matrícula citada não está na lista (o aluno tem outra matrícula no mesmo
  // curso, ou a do push foi trocada): o curso basta para abrir a mesma aula.
  if (target.courseId) {
    return list.find(e => String(e.courseId) === String(target.courseId) &&
      (!target.appId || String(e.applicationId) === String(target.appId))) || null;
  }
  return null;
}

async function ensureEnroll (target) {
  let enroll = findEnroll(target);
  if (enroll) return enroll;
  try {
    await _store.dispatch('enroll/getUserEnroll');
  } catch (e) {}
  enroll = findEnroll(target);
  return enroll || null;
}

function go (location) {
  // push/replace do router estão "patcheados" para resolver com o
  // NavigationFailure em vez de rejeitar (router/index.js).
  return _router.push(location);
}

function isAtClass (enrollId, loId) {
  const route = _router.currentRoute;
  return route && route.name === 'class' &&
    String(route.params.enrollId) === String(enrollId) && String(route.params.id) === String(loId);
}

// Abre um destino já validado (saída de resolvePushTarget/resolveLinkTarget/
// resolveNotificationTarget). Devolve true se a tela pedida abriu.
export async function navigateToTarget (target) {
  if (!target || !_router) return false;

  // Assinatura em atraso: "Minhas assinaturas" do aluno logado, no navegador do
  // app (o mesmo link do menu "Gerenciar Assinaturas"). A tela de fundo fica
  // onde está; sem link (sem assinatura ou erro), o toast já avisou.
  if (target.name === 'subscriptions') {
    const url = await ensureSubscriptionUrl(_store);
    if (!url) return false;
    await openUrl(url);
    return true;
  }

  if (target.name === 'class' || target.name === 'course') {
    const enroll = await ensureEnroll(target);
    if (!enroll) {
      // Matrícula que não é deste aluno (ou foi revogada): a lista de cursos é o
      // único destino honesto.
      if (_router.currentRoute.path !== HOME_PATH) await go({ path: HOME_PATH });
      return false;
    }
    const enrollId = String(enroll.id);

    if (target.name === 'course') {
      // `#certificado` (push/sino de certificado): a página do curso abre o modal.
      const location = { name: 'course', params: { id: enrollId } };
      if (target.hash) location.hash = target.hash;
      await go(location);
      return true;
    }

    // Grade FRESCA da matrícula, mesmo quando ela já é a current: a aula nova de
    // um push de conteúdo não está na grade carregada antes da publicação, e a
    // StudentClass só recarrega a grade quando a matrícula MUDA. De quebra grava
    // o X-ApplicationId certo antes de a aula disparar qualquer request.
    try {
      await _store.dispatch('enroll/setCurrentEnroll', { enrollId });
    } catch (e) {}
    const current = _store.state.enroll.current;
    if (!current || String(current.id) !== enrollId) {
      // setCurrentEnroll falhou (ele mesmo já leva para /p/my-courses e avisa).
      return false;
    }

    const location = { name: 'class', params: { enrollId, id: String(target.loId) } };
    if (target.hash) location.hash = target.hash;
    await go(location);
    return isAtClass(enrollId, target.loId);
  }

  if (target.path) {
    if (_router.currentRoute.path !== target.path) await go({ path: target.path });
    return true;
  }
  return false;
}

// ---- Entradas ----

// Toque numa notificação push (app em background ou morto).
export function handlePushTap (data, options) {
  _pending = { kind: 'push', data: data || {}, options: options || {}, at: Date.now() };
  flushPending();
}

function allowedLinkHosts () {
  const hosts = ['ead.pagtrust.com.br', 'ead.st.pagtrust.com.br'];
  try {
    if (process.env.VUE_APP_EAD_URL) hosts.push(new URL(process.env.VUE_APP_EAD_URL).host);
  } catch (e) {}
  if (typeof window !== 'undefined' && window.location && window.location.host) hosts.push(window.location.host);
  return hosts;
}

// Universal Link (iOS) / App Link (Android). No boot frio o mesmo link chega
// duas vezes — pelo `getLaunchUrl` e pelo `appUrlOpen` retido —, daí o dedupe.
export function handleAppLink (url) {
  if (!url) return;
  const now = Date.now();
  const last = _recentLinks.get(url);
  if (last && now - last < LINK_DEDUPE_MS) return;
  _recentLinks.set(url, now);
  const target = resolveLinkTarget(url, { allowedHosts: allowedLinkHosts() });
  if (!target) {
    console.info('[push] link de entrada ignorado (fora da allowlist):', url);
    return;
  }
  _pending = { kind: 'target', target, at: now };
  flushPending();
}

// Destino vindo de dentro do app (sino, toast): o aluno já está logado.
export function openInternalTarget (target) {
  if (!target) return Promise.resolve(false);
  return navigateToTarget(target).catch(() => false);
}

export function isPushRouterReady () {
  return Boolean(_store && _router);
}
